import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { submitBrandInquiry, errText } from "@/lib/api";
import { Field, TextInput, TextArea, Select, PillGroup, AckCheck, SectionHead, SubmitBar, SuccessPanel, FormShell } from "@/lib/forms";
import { PageHeader } from "@/components/Section";
import { CREATORS } from "@/data/characters";
import { isWindowOpen, windowState } from "@/lib/windows";
import { SealedNotice } from "@/components/recruitment/SealedNotice";
import { UnsealReveal } from "@/components/recruitment/UnsealReveal";

const TYPES = ["Bring a Real Brief", "Sponsor an Issue", "Provide Tools", "Provide a Location", "Provide a Mentor", "Provide an Opportunity"];
const INTENDED_USE = ["Portfolio / educational", "Organic social", "Website", "Digital ads", "Print", "Paid ads", "Internal", "Unsure", "Other"];
const BUDGETS = ["Under $500", "$500–$999", "$1,000–$2,499", "$2,500–$4,999", "$5,000–$9,999", "$10,000+", "Let's discuss"];
const TOOL_ARRANGEMENTS = ["Donation", "Loan", "Discount", "Sponsored access", "In-kind", "Let's discuss"];
const OPP_TYPES = ["Paid freelance", "Contract", "Paid internship", "Apprenticeship", "Part-time", "Full-time", "Shadowing", "Other"];
const DISCIPLINES = CREATORS.map((c) => c.lane);

export default function ApplyBrand() {
  const [mission] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("blv_mission_params") || "null"); } catch { return null; }
  });
  const [v, setV] = useState(() => ({
    contact: {}, partnership_types: [], brief: {}, sponsor: {}, tools: {}, location: {}, mentor: {}, opportunity: {},
    general: mission ? { notes: `MISSION PARAMETERS — Providing: ${mission.providing} · Creative world: ${mission.world} · Objective: ${mission.objective}` } : {},
  }));
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const setIn = (sec, k) => (val) => setV((p) => ({ ...p, [sec]: { ...p[sec], [k]: val } }));
  const has = (t) => v.partnership_types.includes(t);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (v.partnership_types.length === 0) return toast.error("Select at least one way to partner.");
    if (!v.acknowledgment) return toast.error("The partnership acknowledgment is required before submitting.");
    setLoading(true);
    try {
      const res = await submitBrandInquiry(v);
      setDone(res);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        kicker="Brand / Sponsor"
        titleLines={["Bring the Village", "a real idea."]}
        subtitle="Pick every way you'd like to partner — the form adapts to what you select. Fields with a gold star are required."
        dark
      />
      <section className="grain-dark bg-onyx py-14 sm:py-20">
        <div className="mx-auto max-w-[880px] px-4 sm:px-6">
          <Link to="/apply" className="mb-6 inline-flex items-center gap-2 font-head text-sm font-bold uppercase tracking-wide text-goldlight transition-colors hover:text-gold" data-testid="back-to-apply">
            <ArrowLeft size={16} /> All paths
          </Link>

          {!isWindowOpen("brand") ? (
            <SealedNotice kind="brand" />
          ) : done ? (
            <SuccessPanel
              title="Transmission received. Case file created."
              id={done.id}
              note="The Architect will review your request. Our team will reach out to explore what we can build together. This inquiry does not create an agreement — scope, compensation, usage, IP, deliverables, timeline, and credits are established separately."
              emailNotice
            />
          ) : (
            <>
            <UnsealReveal kind="brand" />
            <FormShell marker="big brief energy ✦">
              <p className="mb-6 inline-block border border-gold/40 bg-onyx px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-goldlight" data-testid="window-open-chip">
                Network window open · closes {windowState("brand").closesLabel}
              </p>
              {mission && (
                <p className="mb-6 ml-0 block max-w-full border border-gold/60 bg-charcoal px-3 py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-goldlight sm:ml-2 sm:inline-block" data-testid="mission-params-chip">
                  ● Mission parameters attached — {mission.providing} · {mission.world} · {mission.objective}
                </p>
              )}
              <form onSubmit={submit} className="space-y-5" data-testid="brand-inquiry-form">
                <SectionHead n="01" title="Company & Contact" />
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Company / brand" required><TextInput required value={v.company_name} onChange={(x) => set("company_name", x)} testId="field-company" /></Field>
                  <Field label="Contact name" required><TextInput required value={v.contact_name} onChange={(x) => set("contact_name", x)} testId="field-contact-name" /></Field>
                  <Field label="Email" required><TextInput required type="email" value={v.contact_email} onChange={(x) => set("contact_email", x)} testId="field-contact-email" /></Field>
                  <Field label="Role / title"><TextInput value={v.contact.role_title} onChange={setIn("contact", "role_title")} testId="field-role" /></Field>
                  <Field label="Phone"><TextInput value={v.contact.phone} onChange={setIn("contact", "phone")} testId="field-phone" /></Field>
                  <Field label="Website"><TextInput value={v.contact.website} onChange={setIn("contact", "website")} testId="field-brand-website" /></Field>
                </div>

                <SectionHead n="02" title="How do you want to partner?" blurb="Pick all that apply — sections below unlock based on your choices." />
                <PillGroup multi value={v.partnership_types} onChange={(x) => set("partnership_types", x)} options={TYPES} testId="field-partnership-types" />

                {has("Bring a Real Brief") && (
                  <div data-testid="section-brief">
                    <SectionHead n="B1" title="The Real Brief" />
                    <div className="mt-5 space-y-5">
                      <div className="grid gap-5 sm:grid-cols-2">
                        <Field label="Project type"><TextInput value={v.brief.project_type} onChange={setIn("brief", "project_type")} testId="field-brief-type" placeholder="Campaign, launch, content series..." /></Field>
                        <Field label="Goal"><TextInput value={v.brief.goal} onChange={setIn("brief", "goal")} testId="field-brief-goal" /></Field>
                      </div>
                      <Field label="Describe the project"><TextArea rows={3} value={v.brief.description} onChange={setIn("brief", "description")} testId="field-brief-desc" /></Field>
                      <div className="grid gap-5 sm:grid-cols-2">
                        <Field label="Audience"><TextInput value={v.brief.audience} onChange={setIn("brief", "audience")} testId="field-brief-audience" /></Field>
                        <Field label="Deliverables"><TextInput value={v.brief.deliverables} onChange={setIn("brief", "deliverables")} testId="field-brief-deliverables" /></Field>
                      </div>
                      <Field label="Intended use"><PillGroup multi value={v.brief.intended_use} onChange={setIn("brief", "intended_use")} options={INTENDED_USE} testId="field-brief-use" /></Field>
                    </div>
                  </div>
                )}

                {has("Sponsor an Issue") && (
                  <div data-testid="section-sponsor">
                    <SectionHead n="B2" title="Sponsorship" />
                    <div className="mt-5 grid gap-5 sm:grid-cols-2">
                      <Field label="Support category"><TextInput value={v.sponsor.support_category} onChange={setIn("sponsor", "support_category")} testId="field-sponsor-category" placeholder="Production costs, materials, stipends..." /></Field>
                      <Field label="Budget range"><Select value={v.sponsor.budget_range} onChange={setIn("sponsor", "budget_range")} options={BUDGETS} testId="field-sponsor-budget" /></Field>
                    </div>
                  </div>
                )}

                {has("Provide Tools") && (
                  <div data-testid="section-tools">
                    <SectionHead n="B3" title="Tools & Resources" />
                    <div className="mt-5 space-y-5">
                      <div className="grid gap-5 sm:grid-cols-2">
                        <Field label="Resource type"><TextInput value={v.tools.resource_type} onChange={setIn("tools", "resource_type")} testId="field-tools-type" placeholder="Cameras, software licenses, materials..." /></Field>
                        <Field label="Arrangement"><Select value={v.tools.arrangement} onChange={setIn("tools", "arrangement")} options={TOOL_ARRANGEMENTS} testId="field-tools-arrangement" /></Field>
                        <Field label="Quantity"><TextInput value={v.tools.quantity} onChange={setIn("tools", "quantity")} testId="field-tools-qty" /></Field>
                        <Field label="Availability"><TextInput value={v.tools.availability} onChange={setIn("tools", "availability")} testId="field-tools-availability" /></Field>
                      </div>
                      <Field label="Restrictions"><TextInput value={v.tools.restrictions} onChange={setIn("tools", "restrictions")} testId="field-tools-restrictions" /></Field>
                    </div>
                  </div>
                )}

                {has("Provide a Location") && (
                  <div data-testid="section-location">
                    <SectionHead n="B4" title="Location" />
                    <div className="mt-5 space-y-5">
                      <div className="grid gap-5 sm:grid-cols-2">
                        <Field label="Location name"><TextInput value={v.location.name} onChange={setIn("location", "name")} testId="field-loc-name" /></Field>
                        <Field label="Address"><TextInput value={v.location.address} onChange={setIn("location", "address")} testId="field-loc-address" /></Field>
                        <Field label="Type of space"><TextInput value={v.location.type} onChange={setIn("location", "type")} testId="field-loc-type" placeholder="Studio, storefront, venue..." /></Field>
                        <Field label="Capacity"><TextInput value={v.location.capacity} onChange={setIn("location", "capacity")} testId="field-loc-capacity" /></Field>
                        <Field label="Availability"><TextInput value={v.location.availability} onChange={setIn("location", "availability")} testId="field-loc-availability" /></Field>
                        <Field label="Photo / video permitted?"><Select value={v.location.photo_video_permissions} onChange={setIn("location", "photo_video_permissions")} options={["Yes", "No", "Let's discuss"]} testId="field-loc-permissions" /></Field>
                        <Field label="Cost"><Select value={v.location.cost} onChange={setIn("location", "cost")} options={["Donated", "Discounted", "Standard rate", "Let's discuss"]} testId="field-loc-cost" /></Field>
                      </div>
                      <Field label="Restrictions"><TextInput value={v.location.restrictions} onChange={setIn("location", "restrictions")} testId="field-loc-restrictions" /></Field>
                    </div>
                  </div>
                )}

                {has("Provide a Mentor") && (
                  <div data-testid="section-mentor">
                    <SectionHead n="B5" title="Mentor" />
                    <div className="mt-5 grid gap-5 sm:grid-cols-2">
                      <Field label="Mentor name"><TextInput value={v.mentor.name} onChange={setIn("mentor", "name")} testId="field-mentor-name" /></Field>
                      <Field label="Company / role"><TextInput value={v.mentor.company_role} onChange={setIn("mentor", "company_role")} testId="field-mentor-role" /></Field>
                      <Field label="Area of expertise"><TextInput value={v.mentor.expertise} onChange={setIn("mentor", "expertise")} testId="field-mentor-expertise" /></Field>
                      <Field label="Format"><TextInput value={v.mentor.format} onChange={setIn("mentor", "format")} testId="field-mentor-format" placeholder="Portfolio reviews, workshops, on-set..." /></Field>
                      <Field label="Mode"><Select value={v.mentor.mode} onChange={setIn("mentor", "mode")} options={["Virtual", "In-person", "Either"]} testId="field-mentor-mode" /></Field>
                    </div>
                  </div>
                )}

                {has("Provide an Opportunity") && (
                  <div data-testid="section-opportunity">
                    <SectionHead n="B6" title="Opportunity" />
                    <div className="mt-5 space-y-5">
                      <Field label="Opportunity type"><PillGroup multi value={v.opportunity.types} onChange={setIn("opportunity", "types")} options={OPP_TYPES} testId="field-opp-types" /></Field>
                      <Field label="Disciplines"><PillGroup multi value={v.opportunity.disciplines} onChange={setIn("opportunity", "disciplines")} options={DISCIPLINES} testId="field-opp-disciplines" /></Field>
                      <div className="grid gap-5 sm:grid-cols-2">
                        <Field label="Experience level"><Select value={v.opportunity.experience_level} onChange={setIn("opportunity", "experience_level")} options={["Entry", "Emerging", "Experienced", "Open"]} testId="field-opp-exp" /></Field>
                        <Field label="Pay range"><TextInput value={v.opportunity.pay_range} onChange={setIn("opportunity", "pay_range")} testId="field-opp-pay" /></Field>
                        <Field label="Work setup"><Select value={v.opportunity.location_type} onChange={setIn("opportunity", "location_type")} options={["Remote", "Hybrid", "On location"]} testId="field-opp-setup" /></Field>
                        <Field label="Duration"><TextInput value={v.opportunity.duration} onChange={setIn("opportunity", "duration")} testId="field-opp-duration" /></Field>
                        <Field label="Application deadline"><TextInput value={v.opportunity.deadline} onChange={setIn("opportunity", "deadline")} testId="field-opp-deadline" /></Field>
                        <Field label="Portfolio requirements"><TextInput value={v.opportunity.portfolio_requirements} onChange={setIn("opportunity", "portfolio_requirements")} testId="field-opp-portfolio" /></Field>
                      </div>
                    </div>
                  </div>
                )}

                <SectionHead n="03" title="General" />
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Timing"><TextInput value={v.general.timing} onChange={setIn("general", "timing")} testId="field-gen-timing" placeholder="This quarter, next semester..." /></Field>
                  <Field label="Deadline"><TextInput value={v.general.deadline} onChange={setIn("general", "deadline")} testId="field-gen-deadline" /></Field>
                  <Field label="Budget"><TextInput value={v.general.budget} onChange={setIn("general", "budget")} testId="field-gen-budget" /></Field>
                  <Field label="Open to stipends / compensation?"><Select value={v.general.compensation_openness} onChange={setIn("general", "compensation_openness")} options={["Yes", "No", "Let's discuss"]} testId="field-gen-comp" /></Field>
                  <Field label="How did you hear about us?"><TextInput value={v.general.referral} onChange={setIn("general", "referral")} testId="field-gen-referral" /></Field>
                </div>
                <Field label="Anything else?"><TextArea rows={3} value={v.general.notes} onChange={setIn("general", "notes")} testId="field-gen-notes" /></Field>

                <SectionHead n="04" title="Acknowledgment" />
                <AckCheck checked={v.acknowledgment} onChange={(c) => set("acknowledgment", c)} testId="ack-brand">
                  I understand this inquiry does not create an agreement. Scope, compensation, usage, IP, deliverables, timeline, and credits are established separately.
                </AckCheck>

                <SubmitBar loading={loading} label="Request Network Clearance" loadingLabel="TRANSMITTING TO THE NETWORK..." />
              </form>
            </FormShell>
            </>
          )}
        </div>
      </section>
    </>
  );
}
